/**
 * The dashboard as a spreadsheet.
 *
 * These figures end up in BRSR and internal reporting, which today means
 * someone reads them off the screen and retypes them. That is a transcription
 * step, and transcription steps are where digits go missing.
 *
 * The export is built from what the page has already fetched rather than from
 * fresh requests, so the file says exactly what the reader was looking at. Each
 * cached request carries the title it was made for, which is what labels the
 * rows; and each carries its own window, because the cards read two days and
 * the charts read seven — a single date range in the filename would misdescribe
 * half the file.
 */

/** Columns, in the order a reader scans them. */
export const COLUMNS = [
  'Section',
  'Financial Year',
  'Location',
  'Period',
  'KPI',
  'Value',
  'Unit',
  'Window From',
  'Window To',
];

/**
 * Escapes one field for CSV.
 *
 * A KPI named "Consumption (Awanthi 50 ppm)" is harmless, but meter names and
 * locations are free text and a single comma in one would shift every column
 * after it.
 *
 * @param {*} value - Field value.
 * @returns {string} The field, quoted when it has to be.
 */
const escape = (value) => {
  const text = value === null || value === undefined ? '' : String(value);

  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Joins rows into a CSV document.
 * @param {Array<Array<*>>} rows - Header row first, then body rows.
 * @returns {string} The document, CRLF delimited as the format expects.
 */
export const toCsv = (rows = []) =>
  rows.map((row) => row.map(escape).join(',')).join('\r\n');

/**
 * Reads the rows out of a cached response, whichever shape it arrived in.
 * @param {object} payload - A cached graph response.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * Turns the page's cached requests into spreadsheet rows.
 *
 * @param {Array<Array<*>>} cached - `[queryKey, data]` pairs, as react-query
 *   hands them back. The config sits at `queryKey[1]`.
 * @param {object} scope - What the page was scoped to.
 * @param {string} scope.financialYear - Selected financial year.
 * @param {string} scope.location - Selected location's label, or 'All'.
 * @returns {Array<Array<*>>} Header row followed by one row per figure.
 */
export const exportRows = (cached = [], scope = {}) => {
  const body = [];

  (Array.isArray(cached) ? cached : []).forEach((entry) => {
    const [key, data] = Array.isArray(entry) ? entry : [];
    const config = Array.isArray(key) ? key[1] : null;
    if (!config) return;

    const filters = config.widgetConfig?.filters ?? {};

    rowsOf(data).forEach((row) => {
      body.push([
        config.title ?? '',
        scope.financialYear ?? '',
        scope.location ?? 'All locations',
        row.displayPeriods ?? row.period_ts ?? '',
        row.kpi ?? config.title ?? '',
        row.sum_value ?? row.value ?? '',
        row.unit ?? '',
        filters.startDateTime ?? '',
        filters.endDateTime ?? '',
      ]);
    });
  });

  body.sort((a, b) => String(a[0]).localeCompare(String(b[0])));

  return [COLUMNS, ...body];
};

/**
 * Names the file after what is in it, so a folder of exports stays readable.
 * @param {string} financialYear - Selected financial year.
 * @param {Date} at - When the export was taken.
 * @returns {string} A filename.
 */
export const exportFilename = (financialYear, at = new Date()) => {
  const stamp = at.toISOString().slice(0, 16).replace(/[:T]/g, '-');

  return `water-dashboard-${financialYear || 'all'}-${stamp}.csv`;
};
